/**
 * useFamilyContext Hook
 *
 * Provides family context from the store with loading and error states.
 * Use this hook in family portal pages to get the current family ID.
 */

import { useEffect } from 'react';
import { useFamilyStore, useCurrentFamilyId } from '@/features/family/stores/familyStore';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/core/lib/api';

interface FamilyContextResult {
  familyId: string | null;
  family: any | null;
  isLoading: boolean;
  isInitialized: boolean;
  hasFamily: boolean;
  error: Error | null;
}

/**
 * Hook to get the current family context from the store.
 * Automatically fetches families if not initialized and sets the first one.
 */
export function useFamilyContext(): FamilyContextResult {
  const currentFamilyId = useCurrentFamilyId();
  const currentFamily = useFamilyStore((state) => state.currentFamily);
  const isInitialized = useFamilyStore((state) => state.isInitialized);
  const setCurrentFamily = useFamilyStore((state) => state.setCurrentFamily);
  const setInitialized = useFamilyStore((state) => state.setInitialized);

  // Fetch families for the user
  const { data: familiesData, isLoading, error } = useQuery({
    queryKey: ['families'],
    queryFn: async () => {
      const response = await api.families.list();
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Auto-select first family if none selected
  useEffect(() => {
    if (familiesData?.families && familiesData.families.length > 0) {
      if (!currentFamilyId) {
        setCurrentFamily(familiesData.families[0]);
      }
      setInitialized(true);
    } else if (familiesData && familiesData.families?.length === 0) {
      // No families exist
      setInitialized(true);
    }
  }, [familiesData, currentFamilyId, setCurrentFamily, setInitialized]);

  return {
    familyId: currentFamilyId,
    family: currentFamily,
    isLoading: isLoading && !isInitialized,
    isInitialized,
    hasFamily: !!currentFamilyId,
    error: error as Error | null,
  };
}

export default useFamilyContext;
